import React, { useMemo } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { IconGraphy, Typography, colors } from '@leapeasy/ui-kit';
import styled from 'styled-components';
import { GlobalUXState } from 'components/GlobalUXState';
import { useDemoMask } from 'hooks/useDemoMask';

function toPlainUser(user) {
  if (!user) return null;
  return typeof user.toJS === 'function' ? user.toJS() : user;
}

function resolveActivityEntries(user) {
  const plain = toPlainUser(user);
  if (!plain) return [];

  const entries = plain.activelogs ?? plain.Activelogs ?? plain.ActiveLogs ?? [];

  return Array.isArray(entries) ? entries : [];
}

function formatLoginDate(entry) {
  const value = entry?.createdAt ?? entry?.login ?? entry?.Login;
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function buildUserSubtitle(user, mask) {
  const plain = toPlainUser(user);
  if (!plain) return '';

  const parts = [];
  if (plain.username) parts.push(mask('username', plain.username));
  if (plain.email && plain.email !== plain.username) {
    parts.push(mask('email', plain.email));
  }
  if (plain.sf_landlord_id && !parts.includes(plain.sf_landlord_id)) {
    parts.push(plain.sf_landlord_id);
  }

  if (parts.length > 0) return parts.join(' · ');
  return plain.id ? `User #${plain.id}` : '';
}

export function ActivityLogDetailModal({ open, user, onClose }) {
  const { mask } = useDemoMask();
  const activities = useMemo(() => resolveActivityEntries(user), [user]);
  const subtitle = useMemo(() => buildUserSubtitle(user, mask), [user, mask]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          pr: 1,
          pb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" style={{ color: colors.black[900], fontWeight: 600 }}>
            Activity Logs
          </Typography>
          {subtitle ? (
            <Typography variant="body2" style={{ color: colors.neutral[700], marginTop: 4 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          aria-label="Close activity details"
          onClick={onClose}
          size="small"
          sx={{ mt: 0.25, flexShrink: 0 }}
        >
          <IconGraphy icon="General.Clear" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2 }}>
        {activities.length === 0 ? (
          <EmptyStateWrap>
            <GlobalUXState
              variant="noData"
              compact
              title="No activity details available"
              description="This user has no recorded login activity yet."
            />
          </EmptyStateWrap>
        ) : (
          <ActivityTable>
            <thead>
              <tr>
                <th>#</th>
                <th>IP Address</th>
                <th>Login Date/Time</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((entry, index) => (
                <tr key={entry.id ?? `${entry.userIP || 'ip'}-${formatLoginDate(entry)}-${index}`}>
                  <td>{index + 1}</td>
                  <td>{entry.userIP || '—'}</td>
                  <td>{formatLoginDate(entry)}</td>
                </tr>
              ))}
            </tbody>
          </ActivityTable>
        )}
      </DialogContent>
    </Dialog>
  );
}

const EmptyStateWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0 8px;
`;

const ActivityTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid #eaeaea;
    font-size: 14px;
    color: #333;
  }

  th {
    background: #f8f8f8;
    font-weight: 600;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;
